import React, { useEffect, useState, useContext } from 'react';

import {
  PlaidLinkOnSuccessMetadata,
  PlaidLinkOnExit,
  PlaidLinkOnEvent
} from 'react-plaid-link';
import { debounce, get, omitBy, isNull } from 'lodash';

import AppBridgeCallbacks from '../../../../state/middleware/app-bridge/AppBridgeCallbacks';
import { AnalyticsContext } from '../../../../ModularReduxAnalytics';
import { useApp, useAppDispatchers } from '../../../../state/app';
import { useUser } from '../../../../state/user';
import { useToggles } from '../../../../state/toggles';
import constants from '../constants';

import Button from '../../../../components/UI/Button';
import PlaidLinkContainer from './PlaidLink/container';
import { BaseFundingAccountProps } from '../shared';

import css from '../FundingAccount.module.scss';

const { BANK_NOT_FOUND_STATUS, OAUTH_BANK_LIST } = constants;

enum plaidEventsEnum {
  OPEN = 'OPEN',
  OPEN_OAUTH = 'OPEN_OAUTH',
  FAIL_OAUTH = 'FAIL_OAUTH',
  SUBMIT_CREDENTIALS = 'SUBMIT_CREDENTIALS',
  TRANSITION_VIEW = 'TRANSITION_VIEW',
  LOADING = 'LOADING',
  SEARCH_INSTITUTION = 'SEARCH_INSTITUTION',
  SELECT_INSTITUTION = 'SELECT_INSTITUTION',
  SELECT_ACCOUNT = 'SELECT_ACCOUNT',
  MFA = 'MFA',
  RECAPTCHA = 'RECAPTCHA',
  ERROR = 'ERROR'
}

export interface PlaidProps
  extends Omit<BaseFundingAccountProps, 'fundingSource'> {
  isMorganStanleyFlow: boolean;
  setShowPlaid: (state: boolean) => void;
}

export default function Plaid({
  isMorganStanleyFlow,
  setShowPlaid,
  setFundingSource,
  getInitialFundingAmount,
  submitPaymentMethod
}: PlaidProps) {
  const { analytics } = useContext(AnalyticsContext);
  const { getPlaidToken, answer, startLoading, initialize } =
    useAppDispatchers();

  const {
    regAccessToken,
    registrationId,
    embeddedWebreg,
    appOs,
    registrationToken
  } = useApp();
  const {
    nextRegStep,
    registeredUser,
    partner,
    preregisteredUser,
    tempPhoneNumber,
    role
  } = useUser();
  const { plaidOAuthManualOverrideEnabled, plaidOAuthAndroidLinkEnabled } =
    useToggles();

  const [receivedRedirectUri, setRedirectUri] = useState<string | undefined>(
    undefined
  );
  const [plaidLinkKeyId, setPlaidLinkKeyId] = useState(1);
  const [showPlaidErrorPopup, setShowPlaidErrorPopup] = useState(false);

  const getPlatform = () => {
    if (embeddedWebreg && !plaidOAuthManualOverrideEnabled) {
      if (appOs === 'android') {
        // Android intentionally has another platform that supports the
        // android for WRIA flow explicitly
        if (plaidOAuthAndroidLinkEnabled) {
          return 'android_wria';
        }
        // Avoid using the redirect uri or package name for the non-toggled version
        return 'web';
      }
      if (appOs === 'ios') {
        return 'ios';
      }
    }
    return 'web';
  };

  useEffect(() => {
    getPlaidToken({ platform: getPlatform() });

    // Add Plaid subscriptions to mobile apps
    if (embeddedWebreg) {
      AppBridgeCallbacks.instance.subscribe(
        'setRedirectUri',
        plaidRedirectUri => {
          analytics.trackRUMEvent('Setting Plaid Redirect Uri', {
            eventProps: { plaidRedirectUri }
          });
          setRedirectUri(plaidRedirectUri);
          setPlaidLinkKeyId(plaidLinkKeyId + 1);
        }
      );

      AppBridgeCallbacks.instance.subscribe('exitedNativePlaidFlow', () => {
        // If user closes Plaid in native app then we want to reload Plaid component
        setShowPlaid(false);
        setShowPlaid(true);
      });

      AppBridgeCallbacks.instance.subscribe('nativeAddedPlaid', authToken => {
        const partnerSource = partner || null;
        const regAuthToken = registrationToken || '';
        const preRegUser: any = registeredUser
          ? registeredUser?.user
          : preregisteredUser;

        initialize({
          accessToken: regAccessToken,
          uniqueId: tempPhoneNumber,
          role,
          preregisteredUserId: preRegUser?.id || null,
          authToken: authToken || regAuthToken,
          partnerSource,
          registrationId
        });
      });

      AppBridgeCallbacks.instance.subscribe('showManualACH', () => {
        setShowPlaid(false);
        setFundingSource('ach');
      });
    }

    return () => {
      AppBridgeCallbacks.instance.unsubscribe('setRedirectUri');
      AppBridgeCallbacks.instance.unsubscribe('exitedNativePlaidFlow');
      AppBridgeCallbacks.instance.unsubscribe('showManualACH');
    };
  }, []);

  const savePlaidAccount = (
    plaidPublicToken: string,
    metadata: PlaidLinkOnSuccessMetadata
  ) => {
    startLoading({ isPlaid: true });

    const plaidAccountId = metadata.accounts[0].id;

    submitPaymentMethod(
      {
        plaidPublicToken,
        plaidAccountId,
        source: 'plaid',
        loadAmount: getInitialFundingAmount()
      },
      answer
    );
  };

  const onPlaidExit: PlaidLinkOnExit = (plaidError, metadata) => {
    const status = get(metadata, 'status');
    analytics.trackEvent('IBV Exited', {
      eventProps: {
        step: 'AddFundingAccount',
        during_registration: true
      }
    });
    if (status === BANK_NOT_FOUND_STATUS) {
      // what if a funding source is toggled off? Should we still hide plaid?
      setShowPlaid(false);
      setFundingSource('ach');
      analytics.trackEvent('Reg Save Bank Account Viewed');
      analytics.trackEvent('Add Manual ACH Clicked', {
        eventProps: { during_registration: true }
      });
    }

    if (plaidError && plaidError.error_code === 'INVALID_LINK_TOKEN') {
      getPlaidToken({ platform: getPlatform() });
    }
  };

  const onPlaidSearch = debounce(payload => {
    analytics.trackEvent('IBV Institution Searched', {
      eventProps: {
        institution_id: payload.institution_id,
        institution_name: payload.institution_name,
        institution_search_query: payload.institution_search_query,
        during_registration: true
      }
    });
  }, 200);

  const handleWRIAOAuthErrorIfApplicable = (institution: string) => {
    if (
      embeddedWebreg && // Ensure we're in WRIA
      plaidOAuthManualOverrideEnabled && // Ensure the toggle is on
      OAUTH_BANK_LIST.includes(institution) // Ensure we're only working on an OAuth supported bank.
    ) {
      setShowPlaid(false);
      setShowPlaidErrorPopup(true);
    }
  };

  const onPlaidEvent: PlaidLinkOnEvent = (eventName, metadata) => {
    const viewName = metadata.view_name;
    const payload = omitBy(metadata, isNull);
    const segmentData = {
      during_registration: true,
      institution_id: payload.institution_id,
      institution_name: payload.institution_name
    };

    switch (eventName) {
      case plaidEventsEnum.OPEN:
        analytics.trackEvent('IBV Consent Viewed', {
          eventProps: segmentData
        });
        analytics.trackEvent('IBV Get Started Clicked', {
          eventProps: { during_registration: true }
        });
        break;

      case plaidEventsEnum.TRANSITION_VIEW:
        if (viewName === 'IS_MATCHED_USER') {
          analytics.trackEvent('IBV RUX User Match Decided', {
            eventProps: segmentData
          });
        }
        if (viewName === 'IS_MATCHED_USER_UI') {
          analytics.trackEvent('IBV RUX User Match Completed', {
            eventProps: segmentData
          });
        }
        if (viewName === 'SELECT_INSTITUTION') {
          analytics.trackEvent('IBV Select Institution Viewed', {
            eventProps: segmentData
          });
        }
        break;

      case plaidEventsEnum.LOADING:
        analytics.trackEvent('IBV Loading Viewed', {
          eventProps: segmentData
        });
        break;

      case plaidEventsEnum.SEARCH_INSTITUTION:
        onPlaidSearch(payload);
        break;

      case plaidEventsEnum.SELECT_INSTITUTION:
        handleWRIAOAuthErrorIfApplicable(payload.institution_name);
        analytics.trackEvent('IBV Bank Login Viewed', {
          eventProps: segmentData
        });
        break;

      case plaidEventsEnum.SELECT_ACCOUNT:
        analytics.trackEvent('IBV Select Account Viewed', {
          eventProps: segmentData
        });
        break;

      case plaidEventsEnum.RECAPTCHA:
        analytics.trackEvent('IBV Recaptcha Viewed', {
          eventProps: segmentData
        });
        break;

      case plaidEventsEnum.MFA:
        analytics.trackEvent('IBV MFA Viewed', {
          eventProps: segmentData
        });
        break;

      case plaidEventsEnum.OPEN_OAUTH:
      case plaidEventsEnum.SUBMIT_CREDENTIALS:
      case plaidEventsEnum.FAIL_OAUTH:
        handleWRIAOAuthErrorIfApplicable(payload.institution_name);
        break;

      case plaidEventsEnum.ERROR:
        if (payload.error_type && payload.error_type === 'INSTITUTION_ERROR') {
          setShowPlaid(false);
          setShowPlaidErrorPopup(true);
        }
        payload.next_reg_step = nextRegStep;
        analytics.trackEvent('Plaid Error Triggered', {
          eventProps: {
            error_code: payload.error_code,
            error_type: payload.error_type,
            error_message: payload.error_message,
            institution_id: payload.institution_id,
            institution_name: payload.institution_name,
            next_registration_step: nextRegStep,
            during_registration: true
          }
        });
        break;
      default:
        break;
    }
  };

  return (
    <>
      {showPlaidErrorPopup && (
        <div className={css.plaidErrorPopup}>
          <div className={css.plaidError}>
            <h1 className={css.plaidErrorHeader}>Plaid Error</h1>
            <p>
              Sorry, Plaid is unable to connect to your bank account at this
              time. Please click OK to add your funding source manually.
            </p>
            <Button text="OK" onClick={() => setShowPlaidErrorPopup(false)} />
          </div>
        </div>
      )}
      <PlaidLinkContainer
        key={`plaid_link${plaidLinkKeyId}`}
        receivedRedirectUri={receivedRedirectUri}
        plaidSuccess={savePlaidAccount}
        plaidEvent={onPlaidEvent}
        plaidExit={onPlaidExit}
        isMorganStanleyFlow={isMorganStanleyFlow}
      />
    </>
  );
}
